/**
 * Frontend Tool Handlers — 文件写入/管理类
 *
 * 处理的工具：
 * - write_local_note / write_local_file → 写入文件
 * - rename_local_file → 重命名
 * - create_local_folder → 创建文件夹
 * - move_local_files → 移动文件
 * - delete_local_file → 删除文件
 * - create_folder_and_move → 创建文件夹并移动 (legacy)
 */

import {
  registerFrontendTool,
  type FrontendToolContext,
} from '../frontend-dispatcher';
import {
  scanDirectory,
  buildFileTreeListing,
  renameFileByPath,
  createFolderAndMove,
  createLocalFolderByPath,
  moveLocalFiles,
  writeFileByPath,
  deleteFileByPath,
} from '../../hooks/useFileSystem';

// ─── write_local_note ───────────────────────
registerFrontendTool('write_local_note', async (args, ctx) => {
  if (!args.path || !args.content) return `[Error: Missing path or content argument]`;

  const result = await writeFileByPath(ctx.localDirHandle, args.path, args.content);
  if (result.success) {
    await ctx.refreshFileTree();
    return `Successfully saved note "${args.path}" (${args.content.length} characters). The local file tree has been updated.`;
  }
  return `[Error: Failed to save note "${args.path}": ${result.error}]`;
});

// ─── write_local_file (legacy fallback) ─────
registerFrontendTool('write_local_file', async (args, ctx) => {
  if (!args.path || !args.content) return `[Error: Missing path or content argument]`;

  const result = await writeFileByPath(ctx.localDirHandle, args.path, args.content);
  if (result.success) {
    await ctx.refreshFileTree();
    return `Successfully created file "${args.path}" (${args.content.length} characters). ${args.description || ''} The local file tree has been updated.`;
  }
  return `[Error: Failed to write file "${args.path}": ${result.error}]`;
});

// ─── rename_local_file ──────────────────────
registerFrontendTool('rename_local_file', async (args, ctx) => {
  if (!args.old_path || !args.new_name) return `[Error: Missing old_path or new_name argument]`;

  const result = await renameFileByPath(ctx.localDirHandle, args.old_path, args.new_name);
  if (result.success) {
    await ctx.refreshFileTree();
    return `Successfully renamed "${args.old_path}" to "${args.new_name}". The local file tree has been updated.`;
  }
  return `[Error: Failed to rename "${args.old_path}": ${result.error}]`;
});

// ─── create_local_folder ────────────────────
registerFrontendTool('create_local_folder', async (args, ctx) => {
  if (!args.folder_path) return `[Error: Missing folder_path argument]`;

  const result = await createLocalFolderByPath(ctx.localDirHandle, args.folder_path);
  await ctx.refreshFileTree();
  if (result.success) {
    return `Successfully created folder "${args.folder_path}". The local file tree has been updated.`;
  }
  return `[Error: Failed to create folder "${args.folder_path}": ${result.error}]`;
});

// ─── move_local_files ───────────────────────
registerFrontendTool('move_local_files', async (args, ctx) => {
  if (!args.source_paths || !args.destination_folder) {
    return `[Error: Missing source_paths or destination_folder argument]`;
  }

  const result = await moveLocalFiles(ctx.localDirHandle, args.source_paths, args.destination_folder);
  await ctx.refreshFileTree();
  if (result.success) {
    return `Successfully moved ${result.moved.length} file(s) to "${args.destination_folder}": ${result.moved.join(', ')}. The local file tree has been updated.`;
  }
  return `Partially completed: Moved ${result.moved.length} file(s) to "${args.destination_folder}". Errors: ${result.errors.join('; ')}`;
});

// ─── delete_local_file ──────────────────────
registerFrontendTool('delete_local_file', async (args, ctx) => {
  if (!args.path || !args.confirm_name) return `[Error: Missing path or confirm_name argument]`;

  const result = await deleteFileByPath(ctx.localDirHandle, args.path, args.confirm_name);
  if (result.success) {
    await ctx.refreshFileTree();
    return `Successfully deleted file "${args.path}". The local file tree has been updated.`;
  }
  return `[Error: Failed to delete "${args.path}": ${result.error}]`;
});

// ─── create_folder_and_move (legacy) ────────
registerFrontendTool('create_folder_and_move', async (args, ctx) => {
  if (!args.folder_name || !args.file_paths) return `[Error: Missing folder_name or file_paths argument]`;

  const result = await createFolderAndMove(ctx.localDirHandle, args.folder_name, args.file_paths);
  await ctx.refreshFileTree();
  if (result.success) {
    return `Successfully created folder "${args.folder_name}" and moved ${result.moved.length} files: ${result.moved.join(', ')}. The local file tree has been updated.`;
  }
  return `Partially completed: Moved ${result.moved.length} files to "${args.folder_name}". Errors: ${result.errors.join('; ')}`;
});
